import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { crmIntegrationEndpointApi, wazzupIntegrationApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import { useCrmIntegrationChatUserAccess } from '@/hooks/useCrmIntegrationChatUserAccess'
import { selectProfile } from '@store/profile.reducer'
import { useCRMContext } from '../../CRMContext'
import { getDisabledIntegrationsForCRMPhoneField } from '@pages/Admin/CRMSettings/tabs/ProfilePage/components/ProfileFieldItem/utils'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { buildIntegrationCrmAccessibilityMap } from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import {
  formatCrmChatUnreadBadge,
  getLeadProviderChatMeta,
  getLeadTelephonyEndpoints,
  getProviderUnreadCount,
  getVkLeadChatButtonState,
  getVkUnreadCount,
  leadHasProviderChat,
  VK_LEAD_CHAT_BUTTON_LABEL,
} from '@pages/Admin/CRM/utils/leadFields'
import { isVkIntegrationUnreadBadgesEnabled } from '@pages/Admin/CRM/utils/vkIntegrationLeadChat'
import {
  buildLeadProviderChatMenuAccounts,
  hasAccessibleLeadProviderChatMenuDialog,
} from '@pages/Admin/CRM/utils/leadProviderChatMenu'
import { getCrmIntegrationAccessDeniedTooltip } from '@pages/Admin/CRM/components/integrations/common/lib/crmIntegrationAccessDeniedTooltip'
import {
  CRM_MAX_LEAD_CHAT_INTEGRATION,
  CRM_TG_LEAD_CHAT_INTEGRATION,
  CRM_VK_LEAD_CHAT_INTEGRATION,
  CRM_WAZZUP_LEAD_CHAT_INTEGRATION,
  getCrmIntegrations,
  type CRMIntegrationType,
} from './integrations'
import type {
  PhoneIntegrationButtonsProps,
  TelephonyCallModalState,
  WazzupIframeModalState,
} from './phoneIntegrationButtons.types'
import { sumAccessibleMenuUnread } from './phoneIntegrationButtons.utils'

const INTEGRATION_ACCESS_DENIED_TOOLTIP =
  'Нет доступа к чатам этого лида. Обратитесь к администратору платформы.'

export const usePhoneIntegrationButtons = ({
  leadId,
  fieldId,
  fieldSettings,
  phoneNumber,
  vkLeadSource,
  onOpenVkChat,
  onOpenMaxChat,
  onOpenTelegramChat,
  onOpenWazzupChat,
  vkUnreadCount = 0,
  maxUnreadCount = 0,
  telegramUnreadCount = 0,
  wazzupUnreadCount = 0,
  vkUnreadBadgeSize = 'table',
  vkOnly = false,
  showVkChatButton = true,
  showMaxChatButton = true,
  showTelegramChatButton = true,
  showWazzupChatButton = true,
  showWazzupIntegrations = true,
}: PhoneIntegrationButtonsProps) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const { wazzupIntegrationsData, vkIntegration } = useCRMContext()
  const hasIntegrationChatAccess = useCrmIntegrationChatUserAccess()
  const { connectedIntegrations, isLoading: integrationsLoading, providers } =
    useCrmIntegrationChatAvailability()

  const accessByIntegrationId = useMemo(() => {
    if (integrationsLoading || connectedIntegrations.length === 0) {
      return undefined
    }
    return buildIntegrationCrmAccessibilityMap(connectedIntegrations)
  }, [connectedIntegrations, integrationsLoading])

  const buildMenu = (provider: CrmChatProviderKey) =>
    buildLeadProviderChatMenuAccounts(
      getLeadProviderChatMeta(vkLeadSource, provider),
      provider,
      { accessByIntegrationId },
    )

  const vkChatButton = getVkLeadChatButtonState(vkIntegration, vkLeadSource)
  const showVkUnreadBadge = isVkIntegrationUnreadBadgesEnabled(vkIntegration)

  const vkMenu = buildMenu('vkontakte')
  const maxMenu = buildMenu('max')
  const telegramMenu = buildMenu('telegram')
  const wazzupMenu = buildMenu('wazzup')

  const hasAccessibleVkTabs = hasAccessibleLeadProviderChatMenuDialog(vkMenu)
  const hasAccessibleMaxTabs = hasAccessibleLeadProviderChatMenuDialog(maxMenu)
  const hasAccessibleTelegramTabs =
    hasAccessibleLeadProviderChatMenuDialog(telegramMenu)
  const hasAccessibleWazzupTabs =
    hasAccessibleLeadProviderChatMenuDialog(wazzupMenu)

  const hasInaccessibleVkTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    vkMenu.length > 0 &&
    !hasAccessibleVkTabs
  const hasInaccessibleMaxTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    maxMenu.length > 0 &&
    !hasAccessibleMaxTabs
  const hasInaccessibleTelegramTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    telegramMenu.length > 0 &&
    !hasAccessibleTelegramTabs
  const hasInaccessibleWazzupTabs =
    !integrationsLoading &&
    connectedIntegrations.length > 0 &&
    wazzupMenu.length > 0 &&
    !hasAccessibleWazzupTabs

  const hasMaxChat = leadHasProviderChat(vkLeadSource, 'max')
  const hasTelegramChat = leadHasProviderChat(vkLeadSource, 'telegram')
  const hasWazzupChat = leadHasProviderChat(vkLeadSource, 'wazzup')

  const canShowVkUnreadBadge =
    showVkUnreadBadge && vkChatButton.isEnabled && !hasInaccessibleVkTabs

  const displayVkUnreadCount = canShowVkUnreadBadge
    ? Math.max(
        vkUnreadCount,
        sumAccessibleMenuUnread(vkMenu) || getVkUnreadCount(vkLeadSource),
      )
    : 0

  const showMaxButton =
    showMaxChatButton &&
    hasIntegrationChatAccess &&
    providers.max.isConnected
  const showTelegramButton =
    showTelegramChatButton &&
    hasIntegrationChatAccess &&
    providers.telegram.isConnected
  const showWazzupButton =
    showWazzupChatButton &&
    hasIntegrationChatAccess &&
    providers.wazzup.isConnected

  const displayMaxUnreadCount = hasMaxChat
    ? maxUnreadCount > 0
      ? maxUnreadCount
      : sumAccessibleMenuUnread(maxMenu) ||
        getProviderUnreadCount(vkLeadSource, 'max')
    : 0

  const displayTelegramUnreadCount = hasTelegramChat
    ? telegramUnreadCount > 0
      ? telegramUnreadCount
      : sumAccessibleMenuUnread(telegramMenu) ||
        getProviderUnreadCount(vkLeadSource, 'telegram')
    : 0

  const displayWazzupUnreadCount = hasWazzupChat
    ? wazzupUnreadCount > 0
      ? wazzupUnreadCount
      : sumAccessibleMenuUnread(wazzupMenu) ||
        getProviderUnreadCount(vkLeadSource, 'wazzup')
    : 0

  const telephonyEndpoints = getLeadTelephonyEndpoints(vkLeadSource)

  const [stateModal, setStateModal] = useState<WazzupIframeModalState>({
    open: false,
  })
  const [telephonyModal, setTelephonyModal] = useState<TelephonyCallModalState>({
    endpoint: null,
    extension: '',
    lineNumber: '',
  })
  const [callLoading, setCallLoading] = useState(false)

  const integrations = getCrmIntegrations(wazzupIntegrationsData)

  const disabledIntegrations = useMemo(
    () => getDisabledIntegrationsForCRMPhoneField(fieldSettings),
    [fieldSettings],
  )

  const legacyIntegrations = integrations
    .filter((i) => !i.is_disabled_for_lead)
    .filter((i) => i.is_active && !disabledIntegrations.includes(i.key))

  const startTelephonyCall = async () => {
    const endpoint = telephonyModal.endpoint
    if (!endpoint || !phoneNumber?.trim() || !telephonyModal.extension) return
    setCallLoading(true)
    try {
      const response: any = await crmIntegrationEndpointApi.call(
        endpoint.id,
        {
          lead_id: leadId,
          phone: phoneNumber.trim(),
          extension: telephonyModal.extension,
          line_number: telephonyModal.lineNumber || null,
        },
        { skipGlobalErrorAlert: true },
      )
      if (response?.response) throw response
      setTelephonyModal({ endpoint: null, extension: '', lineNumber: '' })
      addAlert({
        title: 'Звонок запущен',
        text: 'Сначала телефония позвонит сотруднику, затем клиенту',
        type: 'success',
      })
    } catch (error: any) {
      addAlert({
        title: 'Не удалось начать звонок',
        text:
          error?.response?.data?.error?.[0] ||
          error?.response?.data?.message ||
          'Проверьте настройки телефонии и внутренний номер',
        type: 'error',
      })
    } finally {
      setCallLoading(false)
    }
  }

  const openLegacyWazzupIframe = (key: CRMIntegrationType) => {
    // FIXME: потом исправить
    if (key === 'phone') return

    wazzupIntegrationApi
      .generateChatIframe({
        account_id: wazzupIntegrationsData?.account?.id,
        chatType: key,
        phone_field_id: fieldId,
        lead_id: leadId,
        user_id: profile.id,
      })
      .then((res) => {
        if (res?.data?.data?.iframe_url) {
          setStateModal({
            open: true,
            type: key,
            iframeUrl: res.data.data.iframe_url,
          })
        }
      })
      .catch(() =>
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось загрузить интеграцию',
          type: 'error',
        }),
      )
  }

  const showVkProviderButton =
    showVkChatButton &&
    hasIntegrationChatAccess &&
    providers.vkontakte.isConnected

  const providerButtons = {
    vk: showVkProviderButton
      ? {
          icon: CRM_VK_LEAD_CHAT_INTEGRATION.icon,
          iconColor: CRM_VK_LEAD_CHAT_INTEGRATION.color,
          label: VK_LEAD_CHAT_BUTTON_LABEL,
          enabled: vkChatButton.isEnabled && !hasInaccessibleVkTabs,
          disabledTooltip: hasInaccessibleVkTabs
            ? INTEGRATION_ACCESS_DENIED_TOOLTIP
            : vkChatButton.tooltipTitle,
          enabledTooltip: vkChatButton.tooltipTitle,
          unreadLabel: formatCrmChatUnreadBadge(displayVkUnreadCount),
          unreadCount: displayVkUnreadCount,
          menuAccounts: vkMenu,
          accessDeniedTooltip: getCrmIntegrationAccessDeniedTooltip('vkontakte'),
          onOpenChat: onOpenVkChat,
        }
      : null,
    max: showMaxButton
      ? {
          icon: CRM_MAX_LEAD_CHAT_INTEGRATION.icon,
          iconColor: CRM_MAX_LEAD_CHAT_INTEGRATION.color,
          label: CRM_MAX_LEAD_CHAT_INTEGRATION.label,
          enabled: hasMaxChat && !hasInaccessibleMaxTabs,
          disabledTooltip: hasInaccessibleMaxTabs
            ? INTEGRATION_ACCESS_DENIED_TOOLTIP
            : hasMaxChat
              ? null
              : 'Нет чата MAX с лидом',
          enabledTooltip: CRM_MAX_LEAD_CHAT_INTEGRATION.label,
          unreadLabel: formatCrmChatUnreadBadge(displayMaxUnreadCount),
          unreadCount: displayMaxUnreadCount,
          menuAccounts: maxMenu,
          accessDeniedTooltip: getCrmIntegrationAccessDeniedTooltip('max'),
          onOpenChat: onOpenMaxChat,
        }
      : null,
    telegram: showTelegramButton
      ? {
          icon: CRM_TG_LEAD_CHAT_INTEGRATION.icon,
          iconColor: CRM_TG_LEAD_CHAT_INTEGRATION.color,
          label: CRM_TG_LEAD_CHAT_INTEGRATION.label,
          enabled: hasTelegramChat && !hasInaccessibleTelegramTabs,
          disabledTooltip: hasInaccessibleTelegramTabs
            ? INTEGRATION_ACCESS_DENIED_TOOLTIP
            : hasTelegramChat
              ? null
              : 'Нет чата Telegram с лидом',
          enabledTooltip: CRM_TG_LEAD_CHAT_INTEGRATION.label,
          unreadLabel: formatCrmChatUnreadBadge(displayTelegramUnreadCount),
          unreadCount: displayTelegramUnreadCount,
          menuAccounts: telegramMenu,
          accessDeniedTooltip: getCrmIntegrationAccessDeniedTooltip('telegram'),
          onOpenChat: onOpenTelegramChat,
        }
      : null,
    wazzup: showWazzupButton
      ? {
          icon: CRM_WAZZUP_LEAD_CHAT_INTEGRATION.icon,
          iconColor: CRM_WAZZUP_LEAD_CHAT_INTEGRATION.color,
          label: CRM_WAZZUP_LEAD_CHAT_INTEGRATION.label,
          enabled: hasWazzupChat && !hasInaccessibleWazzupTabs,
          disabledTooltip: hasInaccessibleWazzupTabs
            ? INTEGRATION_ACCESS_DENIED_TOOLTIP
            : hasWazzupChat
              ? null
              : 'Нет чата Wazzup с лидом',
          enabledTooltip: CRM_WAZZUP_LEAD_CHAT_INTEGRATION.label,
          unreadLabel: formatCrmChatUnreadBadge(displayWazzupUnreadCount),
          unreadCount: displayWazzupUnreadCount,
          menuAccounts: wazzupMenu,
          accessDeniedTooltip: getCrmIntegrationAccessDeniedTooltip('wazzup'),
          onOpenChat: onOpenWazzupChat,
        }
      : null,
  }

  /** Белый диск — только таблица/канбан; в карточке лида вид как раньше. */
  const useStackedIconLook = vkUnreadBadgeSize !== 'card'

  /** Sipuni/телефония всегда в стопке с чатами (и в карточке тоже). */
  const showTelephonyInStack =
    telephonyEndpoints.length > 0 && (showWazzupIntegrations || vkOnly)

  return {
    vkOnly,
    showWazzupIntegrations,
    phoneNumber,
    vkUnreadBadgeSize,
    useStackedIconLook,
    showTelephonyInStack,
    telephonyEndpoints,
    providerButtons,
    legacyIntegrations,
    openLegacyWazzupIframe,
    stateModal,
    setStateModal,
    telephonyModal,
    setTelephonyModal,
    callLoading,
    startTelephonyCall,
  }
}
